// MethodologyPage.jsx
import React from "react";
import "../styles/MethodologyPage.css";

const PANELS = [
  {
    id: "creativity",
    title: "Creativity",
    color: "#f5b71a",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
        <path fill="currentColor" d="M12 2C10.9 2 10 2.9 10 4c0 .5.2.9.5 1.2C9.6 5.6 8.5 6 7.5 7 6.1 8.4 6 10.4 6 11v1h12v-1c0-.6-.1-2.6-1.5-4-1-1-2.1-1.4-3-1.8.3-.3.5-.7.5-1.2 0-1.1-.9-2-2-2z"/>
        <circle cx="12" cy="17" r="3" fill="currentColor"/>
      </svg>
    ),
    bullets: [
      "Open-ended projects letting learners design games, simulations and apps.",
      "Opportunities to customize and extend sample projects to build ownership.",
      "Story-driven activities that combine animation, sound and interactivity."
    ]
  },
  {
    id: "critical",
    title: "Critical Thinking",
    color: "#ef5033",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
        <path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
    ),
    bullets: [
      "Debugging and problem decomposition practices to build resilience.",
      "Algorithmic thinking: stepwise reasoning and flow breakdown.",
      "Hands-on exercises that connect cause-and-effect with code outcomes."
    ]
  },
  {
    id: "communication",
    title: "Communication",
    color: "#58c7e6",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
        <path fill="currentColor" d="M20 2H4a2 2 0 0 0-2 2v14l4-2h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/>
      </svg>
    ),
    bullets: [
      "Collaborative code reviews and in-class pair critique sessions.",
      "Documentation and storytelling: explaining design decisions clearly.",
      "Presentations and demos to build confidence and audience awareness."
    ]
  },
  {
    id: "collaboration",
    title: "Collaboration",
    color: "#7b47b2",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
        <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM7 11c1.66 0 3-1.34 3-3S8.66 5 7 5 4 6.34 4 8s1.34 3 3 3zM7 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5C14 14.17 9.33 13 7 13zM16 13c-.29 0-.62.02-.97.05C15.37 13 15 13 15 13c-1.66 0-4 1.34-4 3v2h10v-2c0-2.76-2.24-4-4-4z"/>
      </svg>
    ),
    bullets: [
      "Pair programming and peer mentoring to share ideas and feedback.",
      "Project-based teamwork and open-source contribution opportunities.",
      "Hackathons and sprints that encourage rapid iteration and teamwork."
    ]
  }
];

export default function MethodologyPage({ heroImage = "https://user-gen-media-assets.s3.amazonaws.com/gemini_images/98293c3d-230c-45a6-94fd-3cce7752eacf.png" }) {
  return (
    <main className="mp-container">

      {/* HERO */}
      <section
        className="mp-hero"
        style={{ backgroundImage: `url(${heroImage})`, backgroundPosition: "center top" }}
        role="img"
        aria-label="Students collaborating"
      >
        <div className="mp-hero-inner">
          
        </div>

        {/* Smooth wide curve SVG (larger, deeper curve + shadow) */}
        <svg className="mp-hero-curve" viewBox="0 0 1440 220" preserveAspectRatio="none" aria-hidden>
          <defs>
            <filter id="curveShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="-12" stdDeviation="18" floodColor="#000" floodOpacity="0.08"/>
            </filter>
          </defs>

          <path d="M0,160 C180,110 360,220 720,160 C1080,100 1260,180 1440,140 L1440,220 L0,220 Z" fill="#ffffff" filter="url(#curveShadow)" />
        </svg>
      </section>

      <section className="mp-intro mp-intro-rich fade-container" aria-labelledby="methodology-heading">
  <div className="mp-intro-inner">

    <h2 id="methodology-heading" className="mp-intro-title fade-element">
      Our Teaching Methodology
    </h2>

    <h3 className="mp-intro-subtitle fade-element" style={{ animationDelay: "0.15s" }}>
      A Comprehensive Approach to 4C Skill Development
    </h3>

    <p className="mp-intro-paragraph fade-element" style={{ animationDelay: "0.3s" }}>
      At Sprout, we employ a holistic approach to education that fosters the development of the essential 4Cs:
      Creativity, Critical Thinking, Communication, and Collaboration. Our innovative teaching methodology combines
      technology programs, project-based learning techniques, and engaging problem-solving activities to create a
      dynamic and effective learning environment.
    </p>

  </div>
</section>


      {/* PANELS */}
      <section className="mp-panels">
        {PANELS.map((panel) => (
          <article
            key={panel.id}
            className="mp-panel"
            style={{ "--panel-color": panel.color }}
          >
            <div className="mp-panel-head">
              <div
                className="mp-panel-icon"
                style={{
                  backgroundColor: panel.color,
                  boxShadow: `0 6px 18px ${panel.color}33`
                }}
              >
                {panel.icon}
              </div>

              <h3 className="mp-panel-title">{panel.title}</h3>
            </div>

            <ul className="mp-panel-list">
              {panel.bullets.map((b, i) => (
                <li key={i} className="mp-panel-item">
                  <span className="mp-panel-dot" />
                  <span className="mp-panel-text">{b}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </section>

    </main>
  );
}